import { useMutation, useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';
import SettingsPropagationDialog from 'src/components/SettingsPropagationDialog';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchChildPlatformCount,
  propagateSettings,
} from 'src/models/platform/propagation';
import { Identifier } from 'src/types/utilityTypes';
import {
  formatSettingsChanges,
  hasPropagableChanges,
} from 'src/utils/settingsFormatting';
import toast from 'src/utils/toast';

interface HQSettingsPropagationContextType {
  interceptSave: (
    changedFields: Record<string, any>,
    initialValues: Record<string, any>,
    originalSaveFunction: () => Promise<void>,
    t: (key: string, options?: any) => string
  ) => Promise<void>;
  PropagationDialogComponent: React.ReactElement;
}

const HQSettingsPropagationContext = createContext<
  HQSettingsPropagationContextType | undefined
>(undefined);

export const useHQSettingsPropagation = () => {
  const context = useContext(HQSettingsPropagationContext);
  if (!context) {
    throw new Error(
      'useHQSettingsPropagation must be used within HQSettingsPropagationProvider'
    );
  }
  return context;
};

interface HQSettingsPropagationProviderProps {
  children: React.ReactNode;
}

export const HQSettingsPropagationProvider: React.FC<
  HQSettingsPropagationProviderProps
> = ({ children }) => {
  const { session } = useSession();
  const [propagationDialogOpen, setPropagationDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});
  const [pendingInitialValues, setPendingInitialValues] = useState<
    Record<string, any>
  >({});
  const [pendingSaveFunction, setPendingSaveFunction] = useState<
    (() => Promise<void>) | null
  >(null);
  const [currentTranslationFunction, setCurrentTranslationFunction] = useState<
    ((key: string, options?: any) => string) | null
  >(null);

  // Only query child platform count for HQ platforms
  const { data: childPlatformCount = 0 } = useQuery({
    queryKey: ['child-platforms-count', session?.id],
    queryFn: () => fetchChildPlatformCount(session?.id || ''),
    enabled: session?.platformType === 'hq' && !!session?.id,
  });

  // Mutation for propagating settings
  const propagateSettingsMutation = useMutation({
    mutationFn: ({
      platformId,
      changes,
    }: {
      platformId: Identifier;
      changes: Record<string, any>;
    }) => propagateSettings(platformId, changes),
    onSuccess: (result) => {
      if (result.errors.length > 0) {
        toast.error(
          `Settings propagated with errors: ${result.errors.join(', ')}`
        );
      } else {
        toast.success(
          `Settings saved and synced to ${result.successCount} child platforms`
        );
      }
    },
    onError: () => {
      toast.error('Failed to propagate settings to child platforms');
    },
  });

  const interceptSave = async (
    changedFields: Record<string, any>,
    initialValues: Record<string, any>,
    originalSaveFunction: () => Promise<void>,
    t: (key: string, options?: any) => string
  ) => {
    // Only intercept for HQ platforms with children and propagable changes
    if (
      session?.platformType === 'hq' &&
      childPlatformCount > 0 &&
      hasPropagableChanges(changedFields)
    ) {
      // Show propagation dialog instead of saving immediately
      setPropagationDialogOpen(true);
      setPendingChanges(changedFields);
      setPendingInitialValues(initialValues);
      setPendingSaveFunction(() => originalSaveFunction);
      setCurrentTranslationFunction(() => t);
      return;
    }

    // For non-HQ platforms or no propagable changes, just save normally
    await originalSaveFunction();
  };

  const handleSaveOnly = async () => {
    if (pendingSaveFunction) {
      await pendingSaveFunction();
    }
    closePropagationDialog();
  };

  const handleSaveAndSync = async () => {
    // First save the settings
    if (pendingSaveFunction) {
      await pendingSaveFunction();
    }

    // Then propagate to child platforms
    propagateSettingsMutation.mutate(
      { platformId: session?.id || '', changes: pendingChanges },
      {
        onSettled: () => {
          closePropagationDialog();
        },
      }
    );
  };

  const closePropagationDialog = () => {
    setPropagationDialogOpen(false);
    setPendingChanges({});
    setPendingInitialValues({});
    setPendingSaveFunction(null);
    setCurrentTranslationFunction(null);
  };

  const contextValue: HQSettingsPropagationContextType = {
    interceptSave,
    PropagationDialogComponent: (
      <SettingsPropagationDialog
        open={propagationDialogOpen}
        onClose={closePropagationDialog}
        changedSettings={
          currentTranslationFunction
            ? formatSettingsChanges(
                pendingChanges,
                pendingInitialValues,
                currentTranslationFunction
              )
            : []
        }
        childPlatformCount={childPlatformCount}
        onSaveOnly={handleSaveOnly}
        onSaveAndSync={handleSaveAndSync}
      />
    ),
  };

  return (
    <HQSettingsPropagationContext.Provider value={contextValue}>
      {children}
      {contextValue.PropagationDialogComponent}
    </HQSettingsPropagationContext.Provider>
  );
};

// Hook that returns null for non-HQ platforms to keep components clean
export const useOptionalHQSettingsPropagation = () => {
  const { session } = useSession();

  // Always call the hook to maintain hook order, but handle the error gracefully
  const context = useContext(HQSettingsPropagationContext);

  // Only return the context if this is an HQ platform and context exists
  if (session?.platformType !== 'hq' || !context) {
    return null;
  }

  return context;
};
